import { type CSSProperties } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import type { UserFavoriteWithArche } from '@/lib/api';
import { archeIconUrl } from '@/lib/arche-icon-url';

interface FavoriteCardProps {
  favorite: UserFavoriteWithArche;
}

export function FavoriteCard({ favorite }: FavoriteCardProps) {
  const navigate = useNavigate();
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: favorite.archId,
  });

  const style: CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.3 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      className="group relative flex flex-col items-center cursor-pointer touch-none"
      onClick={() => {
        if (!isDragging) navigate(`/a/${favorite.archId}`);
      }}
    >
      {/* Square icon tile */}
      <FavoriteIcon favorite={favorite} />

      {/* Label underneath */}
      <span className="mt-2 text-xs font-medium text-foreground text-center line-clamp-2 w-full px-1">
        {favorite.archeName}
      </span>
    </div>
  );
}

/** Reusable icon visual — also used in DragOverlay */
export function FavoriteIcon({ favorite }: { favorite: UserFavoriteWithArche }) {
  return favorite.archeImageRef ? (
    <img
      src={archeIconUrl(favorite.archeImageRef, 128) ?? ''}
      alt={favorite.archeName}
      className="w-full aspect-square rounded-2xl object-cover shadow-md"
    />
  ) : (
    <div className="w-full aspect-square rounded-2xl bg-linear-to-br from-primary/80 to-primary flex items-center justify-center shadow-md">
      <span className="text-4xl font-semibold text-primary-foreground">
        {favorite.archeName.charAt(0)}
      </span>
    </div>
  );
}
